import { useMemo, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import clsx from 'clsx';
import { useAppSelector } from 'app/hooks';
import useIsMobile from 'hooks/useIsMobile';
import If from 'components/If';
import { getFromBrandConfig } from 'brand-config';
import { selectBrandCode } from 'services/brand/brandSelectors';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { QaLoginPage } from 'testdata/constants';

// @Styles
import { SERVICES } from 'utils/service-entitlements-config';
import { LEGAL_PATHS } from 'utils/constants';
import useIsFeatureAvailable from 'hooks/useIsFeatureAvailable';
import useLegalPath from 'hooks/useLegalPath';
import styles from './Footer.module.scss';

/**
 * Angular shows the enrollment FAQ link only on the activation step, and only for the
 * brands behind its `FaqBrandGuard` (`breachFaqBrands`).
 */
const FAQ_BRANDS = new Set([
  'privacy',
  'psms',
  'iddefense',
  'afs2',
  'mshield',
  'dminor',
]);
const ACTIVATE_PATH_SEGMENT = '/enrollment/activate';

function Footer() {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const isLogin = useAppSelector(selectUserSignInStatus);
  const brandCode = useAppSelector(selectBrandCode);
  const hasInsurance = useIsFeatureAvailable(SERVICES.insurance);
  const termsOfServicePath = useLegalPath('termsOfService');
  const privacyPolicyPath = useLegalPath('privacyPolicy');
  const currentYear = new Date().getFullYear();
  /* The brand config carries the bare legal-entity name; the sentence is assembled here.
     Several brands used to carry `All rights reserved.` in the value too, which rendered
     as "…Pango. All rights reserved.. All rights reserved.". */
  const copyRightNotice = `© ${currentYear} ${getFromBrandConfig('copyRightEntity')}. All rights reserved.`;
  const showTheftText = !!getFromBrandConfig('showTheftText');
  const showInsuranceLink = isLogin && hasInsurance;

  const footerClassNames = clsx(styles.footer, {
    [styles.fixedFooter]: !isMobile,
    [styles.loggedInFooter]: isLogin,
  });

  const showFaqLink = useMemo(() => {
    return (
      pathname.includes(ACTIVATE_PATH_SEGMENT) && FAQ_BRANDS.has(brandCode)
    );
  }, [pathname, brandCode]);

  return (
    <Layout.Footer
      className={footerClassNames}
      data-qa-selector={QaLoginPage.textFooter}
    >
      <ul className={styles.serviceTermsLinks}>
        <li>
          <a
            href={termsOfServicePath}
            target='_blank'
            rel='noreferrer'
            className={styles.link}
            data-qa-selector={QaLoginPage.linkTermsOfService}
          >
            Terms of Service
          </a>
        </li>
        <li>
          <a
            href={privacyPolicyPath}
            target='_blank'
            rel='noreferrer'
            className={styles.link}
            data-qa-selector={QaLoginPage.linkPrivacyPolicy}
          >
            Policies
          </a>
        </li>
        <If condition={showInsuranceLink}>
          <li>
            <Link className={styles.link} to={LEGAL_PATHS.insurance}>
              Insurance
            </Link>
          </li>
        </If>
        <If condition={!isLogin && showFaqLink}>
          <li>
            <Link className={styles.link} to={LEGAL_PATHS.enrollmentFaqs}>
              FAQs
            </Link>
          </li>
        </If>
      </ul>
      <ul className={styles.copyrightText}>
        <li>{copyRightNotice}</li>
        <If condition={showTheftText}>
          <li>No one can prevent all identity theft.</li>
        </If>
      </ul>
    </Layout.Footer>
  );
}

export default memo(Footer);
